// views/Profile/components/TransactionRowDrawer.jsx
import React, { useEffect, useRef, useState } from 'react';
import {
  X,
  Copy,
  Check,
  ArrowDownLeft,
  ArrowUpRight,
} from 'lucide-react';
import './TransactionRowDrawer.css';

const formatCoins = (value) => {
  if (value == null || Number.isNaN(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${new Intl.NumberFormat('en-US').format(value)}`;
};

const formatFullDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const TYPE_META = {
  DEDUCTION: { label: 'Use', tone: 'negative' },
  RECHARGE: { label: 'Recharge', tone: 'positive' },
  REFUND: { label: 'Refund', tone: 'positive' },
  BONUS: { label: 'Bonus', tone: 'accent' },
};

const ID_FIELDS = [
  { key: 'id', label: 'Transaction ID' },
  { key: 'executionId', label: 'Execution ID' },
  { key: 'assistantCallId', label: 'Agent Call ID' },
  { key: 'tableOperationId', label: 'Table Operation ID' },
];

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      timerRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore — clipboard may be unavailable in certain contexts */
    }
  };

  return (
    <button
      type="button"
      className="txn-drawer__copy"
      onClick={handleCopy}
      aria-label={`Copy ${value}`}
    >
      {copied ? (
        <Check size={12} aria-hidden="true" />
      ) : (
        <Copy size={12} aria-hidden="true" />
      )}
    </button>
  );
}

/**
 * TransactionRowDrawer — slides in from the right with the full
 * details of a coin_transactions row. Shows the signed amount in a
 * tone-coded hero, the description, ID fields with copy buttons and
 * the metadata blob pretty-printed.
 *
 * Closes on ESC, click on the backdrop or the X button. Focus trap
 * is intentional but minimal — moves focus to the close button on
 * mount and restores to the previously focused element on unmount.
 *
 * Props:
 *   open:       boolean
 *   transaction: TransactionItem | null
 *   onClose:    () => void
 */
export default function TransactionRowDrawer({
  open,
  transaction,
  onClose,
}) {
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    previousFocusRef.current = document.activeElement;
    closeButtonRef.current?.focus();

    const onKey = (event) => {
      if (event.key === 'Escape') {
        onClose?.();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (previousFocusRef.current && previousFocusRef.current.focus) {
        previousFocusRef.current.focus();
      }
    };
  }, [open, onClose]);

  if (!open || !transaction) {
    return null;
  }

  const typeMeta =
    TYPE_META[transaction.transactionType] ?? {
      label: transaction.transactionType,
      tone: 'neutral',
    };
  const isNegative = (transaction.amount ?? 0) < 0;

  const idFields = ID_FIELDS
    .map((field) => ({ ...field, value: transaction[field.key] }))
    .filter((field) => Boolean(field.value));

  const metadata = transaction.metadata ?? {};
  const hasMetadata = Object.keys(metadata).length > 0;

  return (
    <div
      className="txn-drawer__backdrop"
      onClick={onClose}
      role="presentation"
    >
      <aside
        className="txn-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Transaction details"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="txn-drawer__header">
          <span
            className={`txn-drawer__type-badge txn-drawer__type-badge--${typeMeta.tone}`}
          >
            {isNegative ? (
              <ArrowUpRight size={12} aria-hidden="true" />
            ) : (
              <ArrowDownLeft size={12} aria-hidden="true" />
            )}
            {typeMeta.label}
          </span>
          <button
            ref={closeButtonRef}
            type="button"
            className="txn-drawer__close"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        <div
          className={`txn-drawer__hero${
            isNegative ? ' txn-drawer__hero--negative' : ' txn-drawer__hero--positive'
          }`}
        >
          <span className="txn-drawer__hero-amount">
            {formatCoins(transaction.amount)}
          </span>
          <span className="txn-drawer__hero-suffix">Zenit Coins</span>
        </div>

        <dl className="txn-drawer__list">
          <div className="txn-drawer__row">
            <dt>Date</dt>
            <dd>{formatFullDate(transaction.createdAt)}</dd>
          </div>
          {transaction.resourceType && (
            <div className="txn-drawer__row">
              <dt>Resource</dt>
              <dd>{transaction.resourceType}</dd>
            </div>
          )}
          {transaction.pool && (
            <div className="txn-drawer__row">
              <dt>Pool</dt>
              <dd className="txn-drawer__capitalize">{transaction.pool}</dd>
            </div>
          )}
          {transaction.description && (
            <div className="txn-drawer__row txn-drawer__row--block">
              <dt>Description</dt>
              <dd>{transaction.description}</dd>
            </div>
          )}
        </dl>

        {idFields.length > 0 && (
          <section className="txn-drawer__section">
            <h3 className="txn-drawer__section-title">Identifiers</h3>
            <ul className="txn-drawer__ids">
              {idFields.map((field) => (
                <li key={field.key}>
                  <span className="txn-drawer__id-label">{field.label}</span>
                  <code className="txn-drawer__id-value">{field.value}</code>
                  <CopyButton value={String(field.value)} />
                </li>
              ))}
            </ul>
          </section>
        )}

        {hasMetadata && (
          <section className="txn-drawer__section">
            <h3 className="txn-drawer__section-title">Metadata</h3>
            <pre className="txn-drawer__metadata">
              {JSON.stringify(metadata, null, 2)}
            </pre>
          </section>
        )}
      </aside>
    </div>
  );
}
